import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  rangeDep,
  type SkippedDependency,
} from './types';

/**
 * Parser for Python `requirements.txt` files.
 *
 * `name==version` pins are exact. Any other version specifier (`>=1.0,<2.0`,
 * `~=1.4.2`, `==1.4.*`) is emitted as a range for the Worker to resolve to its
 * upper bound. Entries with no version at all, or that are a
 * reference/editable/URL install, are placed in `skipped` with a reason.
 *
 * Extras in `name[extra1,extra2]==1.0.0` are stripped; only the bare name is
 * kept. Environment markers after `;` are discarded.
 *
 * PEP 503 normalisation is applied to the name (lowercase; runs of `-`, `_`,
 * `.` collapsed to `-`) to match deps.dev's canonical form.
 */

/** PEP 503: normalise a distribution name for lookup. */
function normalizePep503(name: string): string {
  return name.toLowerCase().replace(/[-_.]+/g, '-');
}

/** Strip the extras bracket from a name like `name[extra1,extra2]`. */
function stripExtras(name: string): string {
  return name.replace(/\[.*?\]/, '');
}

/** Join backslash-continued lines into logical lines. */
function joinContinuations(content: string): string {
  // Normalise CRLF first so we only deal with \n.
  return content.replace(/\r\n/g, '\n').replace(/\\\n/g, ' ');
}

/**
 * Classifies a single (joined, stripped) requirement token into an exact `==`
 * pin, a resolvable version range (with the specifier isolated from the rest
 * of the line), or a skip with a reason.
 */
function classifyLine(
  raw: string,
):
  | { kind: 'exact'; name: string; version: string }
  | { kind: 'range'; name: string; specifier: string; raw: string }
  | { kind: 'skip'; name: string | null; reason: string; raw: string } {
  const line = raw.trim();

  // -r / -c / --requirement / --constraint
  if (/^-[rc]\s/i.test(line) || /^--(requirement|constraint)\s/i.test(line)) {
    return { kind: 'skip', name: null, reason: 'nested requirements file', raw: line };
  }

  // Editable installs: -e / --editable
  if (/^-e\s/i.test(line) || /^--editable\s/i.test(line)) {
    return { kind: 'skip', name: null, reason: 'editable install', raw: line };
  }

  // Other flags like --hash, --index-url, --extra-index-url, --find-links, etc.
  if (line.startsWith('--')) {
    return { kind: 'skip', name: null, reason: 'pip flag (not a package)', raw: line };
  }

  // URL / path installs
  if (/^(https?:|git\+|svn\+|hg\+|bzr\+|file:|\.\/|\.\.\/)/i.test(line)) {
    return { kind: 'skip', name: null, reason: 'URL or path install', raw: line };
  }

  // Strip inline --hash=... fragments that can appear after a backslash join.
  // e.g. "requests==2.28.0 --hash=sha256:abc123"
  const withoutHashes = line.replace(/\s+--hash=[^\s]+/g, '').trim();

  // Strip environment markers (everything after the first unquoted semicolon).
  const markerIdx = withoutHashes.indexOf(';');
  const withoutMarkers = (
    markerIdx === -1 ? withoutHashes : withoutHashes.slice(0, markerIdx)
  ).trim();

  // Try to split on the first specifier operator.
  // Match: name[extras]<op>version
  const specMatch = withoutMarkers.match(
    /^([A-Za-z0-9]([A-Za-z0-9._-]*[A-Za-z0-9])?(?:\[[^\]]*\])?)\s*(===?|~=|!=|>=?|<=?|==)(.*)/,
  );

  if (specMatch) {
    // These groups are always present when specMatch is truthy.
    const rawName = specMatch[1] ?? '';
    const op = specMatch[3] ?? '';
    const rest = (specMatch[4] ?? '').trim();

    const bareName = normalizePep503(stripExtras(rawName));
    // The specifier alone (`>=1.0,<2.0`), without the name or any markers —
    // this is what the range resolver parses.
    const specifier = `${op}${rest}`;

    // A single `==` with no wildcard is the only exact form; `==1.2.*`,
    // comma-combined clauses and every other operator are ranges.
    if (op === '==' && !rest.includes(',') && !rest.includes('*')) {
      return { kind: 'exact', name: bareName, version: rest };
    }

    return { kind: 'range', name: bareName, specifier, raw: withoutMarkers };
  }

  // If what remains looks like a bare package name with no specifier.
  const bareMatch = withoutMarkers.match(/^[A-Za-z0-9]([A-Za-z0-9._-]*[A-Za-z0-9])?(\[.*\])?$/);
  if (bareMatch) {
    const bareName = normalizePep503(stripExtras(withoutMarkers));
    return { kind: 'skip', name: bareName, reason: 'no version specified', raw: withoutMarkers };
  }

  // Unknown — treat as a flag or something we cannot parse.
  return { kind: 'skip', name: null, reason: 'unrecognised line', raw: line };
}

/**
 * Parses a `requirements.txt` manifest.
 *
 * Throws `ManifestParseError` only when the input contains no lines that could
 * plausibly be requirement entries at all (e.g. binary data, JSON blob, totally
 * blank content is fine and returns empty arrays).
 */
export const parseRequirementsTxt: ManifestParser = (content: string): ParsedManifest => {
  const joined = joinContinuations(content);
  const rawLines = joined.split('\n');

  const dependencies = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();
  let parseable = 0; // lines that look like they could be requirements
  let total = 0; // non-blank, non-comment lines

  for (const rawLine of rawLines) {
    // Strip inline comments.
    const commentIdx = rawLine.indexOf('#');
    const line = (commentIdx === -1 ? rawLine : rawLine.slice(0, commentIdx)).trim();

    if (line === '') continue;
    total++;

    const result = classifyLine(line);

    if (result.kind === 'exact') {
      parseable++;
      if (!seen.has(result.name)) {
        seen.add(result.name);
        dependencies.push(dep('PYPI', result.name, result.version, line));
      }
    } else if (result.kind === 'range') {
      parseable++;
      if (!seen.has(result.name)) {
        seen.add(result.name);
        dependencies.push(rangeDep('PYPI', result.name, result.specifier));
      }
    } else {
      // Only count as parseable if it is a recognisable pip line (has a name,
      // flag, or URL — not just alien content). 'unrecognised line' reason
      // means it did not look like a pip requirement at all.
      if (result.reason !== 'unrecognised line') {
        parseable++;
      }
      const name = result.name ?? line.slice(0, 60);
      if (!seen.has(name)) {
        seen.add(name);
        skipped.push({ name, requirement: result.raw ?? line, reason: result.reason });
      }
    }
  }

  // Throw only when there are substantial non-blank/non-comment lines that look
  // completely alien (not a single recognisable requirement token). An
  // empty/comment-only file is fine.
  if (total > 0 && parseable === 0) {
    throw new ManifestParseError('Input does not appear to be a requirements.txt file');
  }

  return { system: 'PYPI', dependencies, skipped };
};
